import { useState, useEffect } from 'react';
import { useCartStore } from '../../stores/cartStore';
import { TermsModal } from './TermsModal';
import { OrderService } from '../../services/OrderService';
import { OrderDocumentService } from '../../services/OrderDocumentService';
import { UploadService } from '../../services/UploadService';
import type { UploadProgress, UploadOptions } from '../../types/upload';
import { toast } from 'react-toastify';
import type { CartItem, PrintSpec, OrderData } from '../../types/cart';
import { logError, logEvent } from '../../utils/logger';
import { EmailService } from '../../services/EmailService';
import { useTheme } from '../../hooks/useTheme';
import { ConfirmationModal } from './ConfirmationModal';

interface StepTwoProps {
  onValidChange: (isValid: boolean) => void;
  formData: {
    name: string;
    email: string;
    phone: string;
  };
}

// Constantes para retry e timeouts
const RETRY_ATTEMPTS = 3;
const UPLOAD_TIMEOUT = 30000; // 30 segundos
const TRANSITION_DELAY = 500;

// Componente de status
const UploadStatus = ({ progress, status }: { progress: number; status: string }) => (
  <div className="absolute top-4 left-0 right-0 flex flex-col items-center">
    <div className="text-sm text-cyan-400/80 mb-2">{status}</div>
    <div className="w-64 h-1 bg-gray-800 rounded-full overflow-hidden">
      <div 
        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const UPLOAD_STATES = {
  IDLE: 'idle',
  PREPARING: 'preparing',
  UPLOADING: 'uploading',
  SENDING_EMAIL: 'sending_email',
  COMPLETE: 'complete',
  ERROR: 'error'
} as const;

export function StepTwo({ onValidChange, formData }: StepTwoProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadAttempt, setUploadAttempt] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const cartStore = useCartStore();
  const [networkStatus, setNetworkStatus] = useState<'online' | 'offline'>('online');
  const [uploadState, setUploadState] = useState<keyof typeof UPLOAD_STATES>('IDLE');
  const theme = useTheme(state => state.getThemeStyles());
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  // Monitor de conexão
  useEffect(() => {
    const handleOnline = () => setNetworkStatus('online');
    const handleOffline = () => setNetworkStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const prepareOrderData = (): OrderData => {
    const specs: PrintSpec[] = cartStore.items.map(item => ({
      fileName: item.fileName,
      quantity: item.quantity,
      technology: item.services.technology,
      printer: item.services.printer,
      material: item.services.material,
      color: item.services.color,
      infill: item.services.infill,
      quality: item.services.quality,
      additionalService: item.services.additionalService,
      price: item.price * item.quantity
    }));

    return {
      orderId: '',
      customer: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      },
      items: cartStore.items,
      specs,
      totalAmount: cartStore.totalAmount
    };
  };

  const handleUpload = async () => {
    setShowConfirmation(false);
    const startTime = Date.now();
    let orderData: OrderData = prepareOrderData();

    try {
      // Validações iniciais
      if (networkStatus === 'offline') {
        throw new Error('Sem conexão com a internet');
      }

      if (!cartStore.items.length) {
        logEvent('UPLOAD_ERROR', { message: 'Tentativa de upload sem arquivos' });
        toast.error('Adicione arquivos ao carrinho antes de enviar');
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);
      setUploadState('PREPARING');

      // Log início do upload
      logEvent('UPLOAD_START', {
        filesCount: cartStore.items.length,
        totalSize: cartStore.items.reduce((acc, item) => acc + item.file.size, 0),
        specs: orderData.specs
      });

      // Gerar OrderId
      orderData.orderId = await OrderService.getNextOrderId();
      setUploadProgress(20);
      setUploadStatus('Preparando documentação...');

      // Gerar documento
      const orderDoc = await OrderDocumentService.generateDocument(
        orderData.orderId,
        orderData.customer.name,
        orderData.specs
      );

      setUploadProgress(40);
      setUploadState('UPLOADING');

      // Converter documento para texto
      const orderDocText = await orderDoc.text();

      // Upload com progresso e retry
      const fileName = await UploadService.uploadOrder(
        orderData.orderId,
        orderData.customer.name,
        cartStore.items.map(item => item.file),
        orderDocText,
        {
          onProgress: (progress: number) => {
            const mappedProgress = 50 + (progress * 0.4);
            setUploadProgress(Math.min(90, mappedProgress));
            logEvent('UPLOAD_PROGRESS', { 
              progress: mappedProgress, 
              orderId: orderData.orderId 
            });
          },
          onRetry: (attempt: number, error: Error) => {
            setUploadAttempt(attempt);
            setUploadError(`Tentativa ${attempt}/${RETRY_ATTEMPTS}: ${error.message}`);
            logEvent('UPLOAD_RETRY', { attempt, error: error.message });
          }
        }
      );

      // Enviar email
      setUploadState('SENDING_EMAIL');
      setUploadProgress(95);
      setUploadStatus('Enviando notificação por email...');

      console.log('=== Dados do pedido antes do envio ===', {
        orderId: orderData.orderId,
        customer: orderData.customer,
        itemsCount: orderData.items.length,
        items: orderData.items.map(item => ({
          fileName: item.fileName,
          quantity: item.quantity,
          services: item.services
        }))
      });

      await EmailService.sendOrderNotification(orderData);

      // Sucesso completo
      setUploadProgress(100);
      setUploadState('COMPLETE');
      
      // Log de sucesso
      logEvent('UPLOAD_SUCCESS', {
        orderId: orderData.orderId,
        duration: Date.now() - startTime,
        filesCount: orderData.items.length
      });

      // Limpar carrinho apenas após sucesso completo
      cartStore.clearCart();
      
      onValidChange(true);
      toast.success('Pedido enviado com sucesso!');

    } catch (error) {
      setUploadState('ERROR');
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      logError('UPLOAD_FAILURE', {
        error: errorMessage,
        duration: Date.now() - startTime,
        attempt: uploadAttempt,
        state: uploadState
      });

      console.error('Erro detalhado:', error);
      toast.error(errorMessage);
      onValidChange(false);
      
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmUpload = () => {
    setIsConfirmationModalOpen(true);
  };

  const handleCancelUpload = () => {
    setShowConfirmation(false);
  };

  return (
    <div className={theme.components.upload.wrapper}>
      {networkStatus === 'offline' && (
        <div className="fixed top-0 left-0 right-0 bg-red-500 text-white p-2 text-center">
          Sem conexão com a internet
        </div>
      )}
      
      {showConfirmation && (
        <div className={theme.components.checkbox.confirmation.wrapper}>
          <h3 className={theme.components.checkbox.confirmation.title}>
            Confirmar Envio
          </h3>
          <div className={theme.components.checkbox.confirmation.content.wrapper}>
            <p className={theme.components.checkbox.confirmation.content.text}>
              Confira os dados antes de enviar:
            </p>
            <ul className={theme.components.checkbox.confirmation.content.list}>
              <li>Total de arquivos: {cartStore.items.length}</li>
              <li>Valor total: R$ {cartStore.totalAmount.toFixed(2)}</li>
              <li>Nome: {formData.name}</li>
              <li>Email: {formData.email}</li>
              <li>Telefone: {formData.phone}</li>
            </ul>
          </div>

          <div className={theme.components.checkbox.confirmation.buttons.wrapper}>
            <button
              onClick={handleUpload}
              className={theme.components.checkbox.confirmation.buttons.confirm}
            >
              Confirmar
            </button>
            <button
              onClick={handleCancelUpload}
              className={`
                ${theme.components.modal.footer.button.base}
                ${theme.components.modal.footer.button.enabled}
              `}
            >
              Voltar
            </button>
          </div>
        </div>
      )}

      {/* Círculo de progresso */}
      <div className={theme.components.progress.circular.wrapper}>
        <svg className={theme.components.progress.circular.svg}>
          <circle
            className={theme.components.progress.variants.primary}
            strokeWidth={theme.components.progress.circular.track.width}
            stroke="currentColor"
            fill={theme.components.progress.circular.track.fill}
            r={theme.components.progress.circular.track.radius}
            cx={theme.components.progress.circular.track.center.split(',')[0]}
            cy={theme.components.progress.circular.track.center.split(',')[1]}
          />
          <circle
            className={theme.components.progress.variants.secondary}
            strokeWidth={theme.components.progress.circular.indicator.width}
            strokeDasharray={440}
            strokeDashoffset={440 - (440 * uploadProgress) / 100}
            strokeLinecap={theme.components.progress.circular.indicator.linecap}
            stroke="currentColor"
            fill={theme.components.progress.circular.indicator.fill}
            r={theme.components.progress.circular.indicator.radius}
            cx={theme.components.progress.circular.indicator.center.split(',')[0]}
            cy={theme.components.progress.circular.indicator.center.split(',')[1]}
          />
        </svg>
        <div className={theme.components.progress.circular.text.base}>
          <span className={theme.components.progress.circular.text.value}>
            {uploadProgress}%
          </span>
        </div>
      </div>

      {/* Checkbox e Termos com margem */}
      <div className="mt-8 mb-8">
        <div className={theme.components.checkbox.wrapper}>
          <input
            type="checkbox"
            id="terms"
            checked={isTermsAccepted}
            onChange={(e) => setIsTermsAccepted(e.target.checked)}
            className={theme.components.checkbox.input}
          />
          <label htmlFor="terms" className={theme.components.checkbox.label.text}>
            Aceito os{' '}
            <button
              type="button"
              onClick={() => setIsTermsModalOpen(true)}
              className={theme.components.checkbox.label.button}
            >
              termos de envio
            </button>
          </label>
        </div>
      </div>

      {/* Botão de Upload centralizado */}
      <div className="flex justify-center mt-8">
        <button
          type="button"
          onClick={handleConfirmUpload}
          disabled={!isTermsAccepted}
          className={`
            ${theme.components.button.base}
            ${!isTermsAccepted 
              ? theme.components.button.states.disabled
              : theme.components.button.states.enabled}
            ${theme.components.button.variants.primary}
          `}
        >
          Revisar e Enviar
        </button>
      </div>

      {/* Modal de Termos */}
      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
      />
      <ConfirmationModal 
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleUpload}
        formData={formData}
        cartItems={{
          items: cartStore.items,
          totalAmount: cartStore.totalAmount
        }}
      />
    </div>
  );
}